import type { ApiError, ChangeEvent, ErrorCode } from './types';
import { authBase, config } from './config';

/** An answer that is not 2xx: the server's sentence, its code, the status. */
export class HttpError extends Error {
  constructor(message: string, public status: number, public code: ErrorCode | 'network', public body: Partial<ApiError> = {}) {
    super(message);
  }
}

let csrf: string | null = null;
export const setCsrf = (t: string | null) => (csrf = t);
/** The safety code for a call made outside request() (an upload with progress). */
export const getCsrf = () => csrf;

/** Called when a signed-in page finds its sign-in has ended (after 30 days, or st8n's logout): the app shows "Sign in again". */
let sessionEnded: (() => void) | null = null;
export const onSessionEnded = (fn: (() => void) | null) => (sessionEnded = fn);
const ended = () => {
  const was = csrf !== null;
  csrf = null;
  if (was) sessionEnded?.();
  return new HttpError('Your sign-in ended. Sign in again.', 401, 'unauthenticated');
};

type MockModule = typeof import('../mock/server');
let mock: Promise<MockModule> | null = null;
const demo = () => (mock ??= import('../mock/server'));

/**
 * The one door to Deleg8's API (deleg8/packages/contract): `path` is relative to /api/v1, e.g. "/tickets?list=L-2".
 * Cookie session; x-deleg8-csrf on every change. In demo mode the in-browser server answers the same paths.
 */
export async function request<T = unknown>(method: string, path: string, body?: unknown): Promise<T> {
  if (config.mock) {
    const m = await demo();
    const real = (as?: string) => m.server().handle(as ? 'GET' : method, as ?? path, as ? undefined : body);
    const r = (await (await import('../mock/profile')).profileMock(method, path, body, () => real('/me'))) ?? (await (await import('../mock/portal')).portalMock(method, path, body, real)) ?? (await (await import('../mock/users')).usersMock(method, path, body)) ?? (await (await import('../mock/objects')).objectsMock(method, path, body)) ?? (await (await import('../mock/subtickets')).subMock(method, path, body, real)) ?? (await (await import('../mock/admin')).adminMock(method, path, body)) ?? (await m.server().handle(method, path, body));
    await new Promise((res) => setTimeout(res, 40 + Math.random() * 80));
    if (r.status >= 400) {
      const b = r.body as ApiError;
      throw new HttpError(b.error, r.status, b.code, b);
    }
    return structuredClone(r.body) as T;
  }
  for (let attempt = 0; ; attempt++) {
    const headers: Record<string, string> = {};
    if (body !== undefined) headers['content-type'] = 'application/json';
    if (method !== 'GET' && csrf) headers['x-deleg8-csrf'] = csrf;
    let r: Response;
    try {
      r = await fetch(config.apiBase + path, { method, headers, body: body === undefined ? undefined : JSON.stringify(body), credentials: 'include' });
    } catch {
      throw new HttpError('Deleg8 could not be reached. Check your connection.', 0, 'network');
    }
    const j = (await r.json().catch(() => ({}))) as Partial<ApiError>;
    if (r.ok) return j as T;
    // The safety code is read once, so a sign-in in another tab leaves this one with an old code: fetch the new one, try once more.
    if (r.status === 403 && j.code === 'csrf' && attempt === 0 && method !== 'GET' && (await freshCsrf())) continue;
    if (r.status === 401 && j.code === 'unauthenticated') throw ended();
    throw new HttpError(j.error || words(r.status), r.status, (j.code as ErrorCode) || 'internal', j);
  }
}

async function freshCsrf(): Promise<boolean> {
  try {
    const r = await fetch(config.apiBase + '/me', { credentials: 'include' });
    if (r.status === 401) throw ended();
    const m = (await r.json().catch(() => null)) as { csrfToken?: string } | null;
    if (!m?.csrfToken) return false;
    csrf = m.csrfToken;
    return true;
  } catch (e) {
    if (e instanceof HttpError) throw e;
    return false;
  }
}

function words(status: number) {
  if (status === 401) return 'Your session ended. Sign in again.';
  if (status === 404) return 'That is not there, or you cannot see it.';
  if (status === 429) return 'Too many requests. Wait a moment.';
  return 'Something went wrong. Try again.';
}

export const get = <T>(path: string) => request<T>('GET', path);
export const post = <T>(path: string, body: unknown = {}) => request<T>('POST', path, body);
export const patch = <T>(path: string, body: unknown) => request<T>('PATCH', path, body);
export const put = <T>(path: string, body: unknown) => request<T>('PUT', path, body);
export const del = <T>(path: string) => request<T>('DELETE', path);

export const errText = (e: unknown) => (e instanceof Error ? e.message : 'Something went wrong.');

/** Sign in: the demo and local dev log straight in; otherwise Sign in with st8n. */
export async function signIn(next = location.pathname + location.search) {
  if (config.mock || config.devLoginEmail) {
    const email = config.devLoginEmail ?? 'robin@example.com';
    if (config.mock) await (await demo()).server().handle('POST', '/auth/dev/login', { email });
    else await fetch(authBase() + '/dev/login', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ email }), credentials: 'include' });
    return true;
  }
  location.assign(`${authBase()}/sign-in?next=${encodeURIComponent(next)}`);
  return false;
}

export async function signOut() {
  const was = csrf;
  csrf = null;
  if (config.mock) await (await demo()).server().handle('POST', '/auth/logout');
  else await fetch(authBase() + '/logout', { method: 'POST', headers: was ? { 'x-deleg8-csrf': was } : {}, credentials: 'include' }).catch(() => {});
}

/** Live changes: GET /events (event: tickets, never ticket text). Returns an unsubscribe. */
/** A customer's page has no live stream (it is the internal board's); the portal refreshes by itself instead. */
let noLive = false;
export const disableLive = () => { noLive = true; };
export function subscribe(fn: (e: ChangeEvent) => void): () => void {
  if (noLive) return () => {};
  if (config.mock) {
    let off = () => {};
    let gone = false;
    void demo().then((m) => {
      if (!gone) off = m.server().on(fn);
    });
    return () => {
      gone = true;
      off();
    };
  }
  // One stream for the whole page: browsers allow ~6 connections per host, and every screen listens.
  listeners.add(fn);
  if (!stream && !retryTimer) openStream();
  return () => {
    listeners.delete(fn);
    if (!listeners.size) {
      stream?.close();
      stream = null;
      if (retryTimer) clearTimeout(retryTimer);
      retryTimer = null;
      attempt = 0;
      dropped = false;
      setStreamDown(false);
    }
  };
}
const listeners = new Set<(e: ChangeEvent) => void>();
let stream: EventSource | null = null;
let retryTimer: ReturnType<typeof setTimeout> | null = null;
let attempt = 0;
/** After the connection dropped and came back, changes may have been missed: tell every screen to fetch again. */
let dropped = false;
/** A dropped (or refused) live stream: the app shows a banner until it is back. */
let streamDown = false;
const downListeners = new Set<(down: boolean) => void>();
const setStreamDown = (d: boolean) => {
  if (d === streamDown) return;
  streamDown = d;
  for (const l of [...downListeners]) l(d);
};
export function onStreamDown(fn: (down: boolean) => void): () => void {
  downListeners.add(fn);
  fn(streamDown);
  return () => void downListeners.delete(fn);
}
const CLOSED = 2; // EventSource.CLOSED: the browser gave up (a non-200 answer, a wrong type); it never retries by itself.

function openStream() {
  const es = new EventSource(config.apiBase + '/events', { withCredentials: true });
  stream = es;
  es.addEventListener('error', () => {
    dropped = true;
    setStreamDown(true);
    if (es.readyState !== CLOSED || stream !== es) return;
    // Closed for good: make a new one after 1 s, 2 s, 4 s ... at most 30 s.
    es.close();
    stream = null;
    const wait = Math.min(30_000, 1000 * 2 ** attempt++);
    retryTimer = setTimeout(() => {
      retryTimer = null;
      if (listeners.size) openStream();
    }, wait);
  });
  es.addEventListener('open', () => {
    attempt = 0;
    setStreamDown(false);
    if (!dropped) return;
    dropped = false;
    for (const l of [...listeners]) l({ ids: [], lists: [], type: 'reconnected' });
  });
  es.addEventListener('tickets', ((m: MessageEvent) => {
    let e: ChangeEvent;
    try {
      e = JSON.parse(m.data) as ChangeEvent;
    } catch {
      return;
    }
    for (const l of [...listeners]) l(e);
  }) as EventListener);
}
